import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Alert,
  CircularProgress,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Vehicle, VehicleConditionAssessment, VehicleConditionAssessmentFormData, VehicleConditionPhotoFormData, VehicleConditionNoteFormData, VehicleConditionPhoto, SubClient } from '../../types';
import { useAppDispatch, useAppSelector } from '../../store';
import { createAssessment, updateAssessment, fetchAssessmentHistory, clearError } from '../../store/vehicleConditionAssessmentSlice';
import { fetchConditionPhotoTypes } from '../../store/conditionPhotoTypeSlice';
import { fetchConditionNoteTypes } from '../../store/conditionNoteTypeSlice';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PhotoCaptureModal from './PhotoCaptureModal';
import PhotoEditModal from './PhotoEditModal';
import SubClientSearchModal from './SubClientSearchModal';
import SubClientCreateModal from './SubClientCreateModal';
import SignatureModal from './SignatureModal';

const DEFAULT_PHOTO_TYPES = [
  { value: 'exterior_front', label: 'Avant', icon: '🚗' },
  { value: 'exterior_rear', label: 'Arrière', icon: '🚙' },
  { value: 'exterior_left', label: 'Côté gauche', icon: '🚘' },
  { value: 'exterior_right', label: 'Côté droit', icon: '🚖' },
  { value: 'interior_dashboard', label: 'Tableau de bord', icon: '📊' },
  { value: 'interior_seats', label: 'Sièges', icon: '🪑' },
  { value: 'interior_trunk', label: 'Coffre', icon: '📦' },
  { value: 'damage_detail', label: 'Détail dommage', icon: '🔧' },
  { value: 'other', label: 'Autre', icon: '📷' }
];

interface ConditionAssessmentModalProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  assessmentToEdit?: VehicleConditionAssessment | null;
}

const ConditionAssessmentModal: React.FC<ConditionAssessmentModalProps> = ({
  vehicle,
  open,
  onClose,
  onSuccess,
  assessmentToEdit
}) => {
  const dispatch = useAppDispatch();
  const { creating, updating, error, assessmentHistory } = useAppSelector(state => state.vehicleConditionAssessments);
  const isEditMode = Boolean(assessmentToEdit);

  // Form state
  const [formData, setFormData] = useState<VehicleConditionAssessmentFormData>({
    assessment_date: format(new Date(), 'yyyy-MM-dd'),
    assessment_type: null,
    status: 'good',
    sub_client_id: null,
    general_notes: '',
    damage_notes: '',
    alert_notes: '',
    photos: [],
    notes: []
  });

  // UI state
  const [activeTab, setActiveTab] = useState<'photos' | 'notes' | 'general'>('general');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<VehicleConditionAssessment | null>(null);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [showSubClientSearch, setShowSubClientSearch] = useState(false);
  const [showSubClientCreate, setShowSubClientCreate] = useState(false);
  const [selectedSubClient, setSelectedSubClient] = useState<SubClient | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [photoToUpdateIndex, setPhotoToUpdateIndex] = useState<number | null>(null);
  const [departAssessment, setDepartAssessment] = useState<VehicleConditionAssessment | null>(null);
  const [isPhotoEditorOpen, setIsPhotoEditorOpen] = useState(false);
  const [photoEditorImage, setPhotoEditorImage] = useState<string | null>(null);
  const [photoEditorIndex, setPhotoEditorIndex] = useState<number | null>(null);

  const { types: conditionPhotoTypes, loading: photoTypesLoading } = useAppSelector(state => state.conditionPhotoTypes);
  // Get condition note types from Redux store
  const { types: conditionNoteTypes, loading: noteTypesLoading } = useAppSelector(state => state.conditionNoteTypes);
  
  const photoTypeOptions = useMemo(() => {
    if (conditionPhotoTypes.length > 0) {
      return conditionPhotoTypes
        .filter(type => type.is_active)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map(type => ({
          value: type.code,
          label: type.name,
          icon: type.icon || '📷',
        }));
    }
    return DEFAULT_PHOTO_TYPES;
  }, [conditionPhotoTypes]);

  const defaultPhotoTypeValue = photoTypeOptions[0]?.value || 'other';

  // Map condition note types to the format expected by the UI
  const noteTypes = conditionNoteTypes.map(type => ({
    value: type.id.toString(), // Use ID as value instead of hardcoded string
    label: type.name,
    icon: type.icon,
    color: type.color
  }));

  // Load assessment history and note types when modal opens
  useEffect(() => {
    if (open && vehicle) {
      dispatch(fetchAssessmentHistory(vehicle.id));
      // Fetch condition note types if not already loaded
      if (conditionNoteTypes.length === 0) {
        dispatch(fetchConditionNoteTypes());
      }
      if (conditionPhotoTypes.length === 0) {
        dispatch(fetchConditionPhotoTypes());
      }
    }
  }, [open, vehicle, dispatch, conditionNoteTypes.length, conditionPhotoTypes.length]);

  // Find corresponding depart assessment when creating a retour
  useEffect(() => {
    if (open && !isEditMode && assessmentHistory) {
      const allAssessments = Object.values(assessmentHistory).flat();
      
      const departAssessments = allAssessments
        .filter(a => a.assessment_type === 'depart')
        .sort((a, b) => {
          const dateA = new Date(a.assessment_date).getTime();
          const dateB = new Date(b.assessment_date).getTime();
          return dateB - dateA; // Newest first
        });
      
      // Get the most recent depart assessment (always load it, even if not retour yet)
      const latestDepart = departAssessments[0];
      if (latestDepart) {
        // Ensure photos are loaded - check if it's an array or needs to be accessed differently
        let photos: VehicleConditionPhoto[] = [];
        if (Array.isArray(latestDepart.photos)) {
          photos = latestDepart.photos;
        } else if (latestDepart.photos && typeof latestDepart.photos === 'object') {
          // If it's an object (like a collection), convert to array
          photos = Object.values(latestDepart.photos) as VehicleConditionPhoto[];
        }
        
        // Create a copy with photos as an array
        const departWithPhotos: VehicleConditionAssessment = {
          ...latestDepart,
          photos: photos
        };
        
        setDepartAssessment(departWithPhotos);
      } else {
        setDepartAssessment(null);
      }
    } else {
      setDepartAssessment(null);
    }
  }, [open, assessmentHistory, isEditMode, formData.assessment_type]);

  // Populate form when editing
  useEffect(() => {
    if (open && assessmentToEdit) {
      // Load existing photos for display
      const existingPhotos: VehicleConditionPhotoFormData[] = (assessmentToEdit.photos || []).map(photo => ({
        photo_path: photo.file_url || '', // Use file_url for display
        type: photo.type,
        description: photo.description || '',
        // No file object for existing photos
      }));
      
      // Load existing notes for display
      const existingNotes: VehicleConditionNoteFormData[] = (assessmentToEdit.notes || []).map(note => ({
        type: note.type,
        content: note.content,
      }));
      
      setFormData({
        assessment_date: format(new Date(assessmentToEdit.assessment_date), 'yyyy-MM-dd'),
        assessment_type: assessmentToEdit.assessment_type || null,
        status: assessmentToEdit.status,
        sub_client_id: assessmentToEdit.sub_client_id || null,
        general_notes: assessmentToEdit.general_notes || '',
        damage_notes: assessmentToEdit.damage_notes || '',
        alert_notes: assessmentToEdit.alert_notes || '',
        photos: existingPhotos, // ✅ Load existing photos
        notes: existingNotes // ✅ Load existing notes
      });
      
      // Load sub-client if exists
      if (assessmentToEdit.sub_client) {
        setSelectedSubClient(assessmentToEdit.sub_client);
      } else if (assessmentToEdit.sub_client_id) {
        // Fetch sub-client if not loaded
        // This will be handled by the component if needed
      }
    } else if (open) {
      // Reset form for new assessment
      setFormData({
        assessment_date: format(new Date(), 'yyyy-MM-dd'),
        assessment_type: null,
        status: 'good',
        sub_client_id: null,
        general_notes: '',
        damage_notes: '',
        alert_notes: '',
        photos: [],
        notes: []
      });
      setSelectedSubClient(null);
    }
  }, [open, assessmentToEdit]);

  // Clear error when modal closes
  useEffect(() => {
    if (!open) {
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const handleInputChange = (field: keyof VehicleConditionAssessmentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddPhoto = (updateIndex?: number) => {
    // Créer un input file temporaire
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = updateIndex === undefined; // Only allow multiple if adding new photos
    
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        if (updateIndex !== undefined && formData.photos && formData.photos[updateIndex]) {
          // Update existing photo - only take first file
          const file = files[0];
          const reader = new FileReader();
          reader.onload = (event) => {
            setFormData(prev => ({
              ...prev,
              photos: prev.photos?.map((photo, i) => 
                i === updateIndex 
                  ? { ...photo, photo_path: event.target?.result as string, file: file }
                  : photo
              ) || []
            }));
            setPhotoToUpdateIndex(null);
          };
          reader.readAsDataURL(file);
        } else {
          // Add new photo(s)
          Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onload = (event) => {
              const newPhoto: VehicleConditionPhotoFormData = {
                photo_path: event.target?.result as string,
                type: defaultPhotoTypeValue,
                description: '',
                file: file // Garder la référence du fichier pour l'upload
              };
              setFormData(prev => ({
                ...prev,
                photos: [...(prev.photos || []), newPhoto]
              }));
            };
            reader.readAsDataURL(file);
          });
        }
      }
    };
    
    input.click();
  };

  const handleCapturePhoto = (imageDataUrl: string, file: File) => {
    // If we're updating an existing photo (from template), update it instead of adding new
    if (photoToUpdateIndex !== null && formData.photos && formData.photos[photoToUpdateIndex]) {
      setFormData(prev => ({
        ...prev,
        photos: prev.photos?.map((photo, i) => 
          i === photoToUpdateIndex 
            ? { ...photo, photo_path: imageDataUrl, file: file }
            : photo
        ) || []
      }));
      setPhotoToUpdateIndex(null);
    } else {
      // Add new photo
      const newPhoto: VehicleConditionPhotoFormData = {
        photo_path: imageDataUrl,
        type: defaultPhotoTypeValue,
        description: '',
        file: file // Garder la référence du fichier pour l'upload
      };
      setFormData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), newPhoto]
      }));
    }
  };

  const handleOpenPhotoEditor = (index: number) => {
    const targetPhoto = formData.photos?.[index];
    if (!targetPhoto?.photo_path) {
      return;
    }
    setPhotoEditorIndex(index);
    setPhotoEditorImage(targetPhoto.photo_path);
    setIsPhotoEditorOpen(true);
  };

  const handleClosePhotoEditor = () => {
    setIsPhotoEditorOpen(false);
    setPhotoEditorImage(null);
    setPhotoEditorIndex(null);
  };

  const handlePhotoEditSave = (editedImageDataUrl: string, file: File) => {
    if (photoEditorIndex === null) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      photos: prev.photos?.map((photo, idx) =>
        idx === photoEditorIndex ? { ...photo, photo_path: editedImageDataUrl, file } : photo
      ) || []
    }));
    handleClosePhotoEditor();
  };

  const handleUpdatePhoto = (index: number, field: keyof VehicleConditionPhotoFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos?.map((photo, i) => 
        i === index ? { ...photo, [field]: value } : photo
      ) || []
    }));
  };

  const handleRemovePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos?.filter((_, i) => i !== index) || []
    }));
  };

  const handleAddNote = () => {
    // Use the first available note type, or a default value
    const defaultType = noteTypes.length > 0 ? noteTypes[0].value : '1';
    const newNote: VehicleConditionNoteFormData = {
      type: defaultType,
      content: ''
    };
    setFormData(prev => ({
      ...prev,
      notes: [...(prev.notes || []), newNote]
    }));
  };

  const handleUpdateNote = (index: number, field: keyof VehicleConditionNoteFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      notes: prev.notes?.map((note, i) => 
        i === index ? { ...note, [field]: value } : note
      ) || []
    }));
  };

  const handleRemoveNote = (index: number) => {
    setFormData(prev => ({
      ...prev,
      notes: prev.notes?.filter((_, i) => i !== index) || []
    }));
  };

  const handleLoadTemplate = (assessment: VehicleConditionAssessment) => {
    // Load notes from previous assessment
    // The type field is stored as a string (note type ID)
    const loadedNotes: VehicleConditionNoteFormData[] = (assessment.notes || []).map(note => {
      // Ensure type is a string (convert to string if somehow it's not)
      const noteType = String(note.type || '');
      
      // Verify the note type exists in available types
      const typeExists = noteTypes.some(t => t.value === noteType);
      if (!typeExists && noteType) {
        console.warn(`Note type "${noteType}" not found in available types. Available types:`, noteTypes.map(t => t.value));
      }
      
      return {
        type: noteType,
        content: note.content || '',
      };
    });
    
    // Load photos from previous assessment
    // Import the actual photos with their URLs so they appear automatically
    // For retour: also fetch and convert to File objects so they can be submitted
    // User can then replace them or add annotations
    // Check both current formData type and the assessment being loaded (in case user loads template before selecting type)
    const isRetour = formData.assessment_type === 'retour' || assessment.assessment_type === 'retour';
    const loadPhotos = async () => {
      const photos: VehicleConditionPhotoFormData[] = [];
      
      for (const photo of (assessment.photos || [])) {
        // Import the actual photo URL so it appears in the form
        const photoUrl = (photo.file_url || (photo.file_path ? `/api/storage/vehicle-assessments/${photo.file_path}` : '')) 
          ? (photo.file_url || `/api/storage/vehicle-assessments/${photo.file_path}`)
          : '';
        
        let photoFile: File | undefined = undefined;
        
        // For retour, fetch the image and convert to File so it can be submitted
        if (isRetour && photoUrl) {
          try {
            const response = await fetch(photoUrl);
            const blob = await response.blob();
            const fileName = photo.file_path || `photo_${Date.now()}.jpg`;
            photoFile = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          } catch (error) {
            console.error('Failed to fetch photo for import:', error);
          }
        }
        
        photos.push({
          photo_path: photoUrl, // Import actual photo URL so it appears
          type: photo.type || '',
          description: photo.description || '',
          file: photoFile, // For retour: include File object so it can be submitted
        });
      }
      
      return photos;
    };
    
    // Load photos asynchronously
    loadPhotos().then(loadedPhotos => {
      console.log('Loading template from assessment:', {
        assessmentId: assessment.id,
        notesCount: assessment.notes?.length || 0,
        photosCount: assessment.photos?.length || 0,
        loadedNotes,
        loadedPhotos,
        isRetour,
        availableNoteTypes: noteTypes.map(t => ({ value: t.value, label: t.label }))
      });
      
      setFormData(prev => ({
        ...prev,
        general_notes: assessment.general_notes || '',
        damage_notes: assessment.damage_notes || '',
        alert_notes: assessment.alert_notes || '',
        sub_client_id: assessment.sub_client_id || prev.sub_client_id,
        notes: loadedNotes, // Load the notes array
        photos: loadedPhotos // Load the photos array with types, descriptions, URLs, and Files (for retour)
      }));
      
      if (assessment.sub_client) {
        setSelectedSubClient(assessment.sub_client);
      }
      setSelectedTemplate(assessment);
      setShowTemplateSelector(false);
      
      // Switch to photos tab if photos were loaded, otherwise notes tab if notes were loaded
      if (loadedPhotos.length > 0) {
        setActiveTab('photos');
      } else if (loadedNotes.length > 0) {
        setActiveTab('notes');
      }
    });
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.assessment_type) {
      alert('Veuillez sélectionner un type d\'évaluation (Départ ou Retour)');
      return;
    }
    
    // For new assessments, show signature modal first
    if (!isEditMode) {
      setShowSignatureModal(true);
      return;
    }
    
    // For edit mode, submit directly (no signature required for edits)
    handleSubmitAfterSignature(null);
  };

  const handleSignatureSave = (dataUrl: string, file: File) => {
    console.log('Signature saved:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      dataUrlLength: dataUrl.length
    });
    setSignatureDataUrl(dataUrl);
    setSignatureFile(file);
    setShowSignatureModal(false);
    // Continue with submission after signature is saved
    // Pass the file directly to avoid React state timing issues
    handleSubmitAfterSignature(file);
  };

  const handleSubmitAfterSignature = async (signatureFileToUse?: File | null) => {
    try {
      // Validate required fields
      if (!formData.assessment_type) {
        alert('Veuillez sélectionner un type d\'évaluation (Départ ou Retour)');
        return;
      }
      
      if (isEditMode && assessmentToEdit) {
        // UPDATE existing assessment
        // First update basic fields
        const updateData = {
          assessment_date: formData.assessment_date,
          assessment_type: formData.assessment_type,
          status: formData.status,
          sub_client_id: formData.sub_client_id || null,
          general_notes: formData.general_notes || '',
          damage_notes: formData.damage_notes || '',
          alert_notes: formData.alert_notes || ''
        };
        
        await dispatch(updateAssessment({ 
          vehicleId: vehicle.id, 
          assessmentId: assessmentToEdit.id,
          data: updateData 
        })).unwrap();
        
        // TODO: Handle adding new photos/notes to existing assessment
        // This would require separate API calls to:
        // - POST /vehicles/{id}/condition-assessments/{assessmentId}/photos
        // - POST /vehicles/{id}/condition-assessments/{assessmentId}/notes
        
      } else {
        // CREATE new assessment with photos and notes
        const formDataToSend = new FormData();
        
        // Ajouter les champs de base
        formDataToSend.append('assessment_date', formData.assessment_date);
        formDataToSend.append('assessment_type', formData.assessment_type); // Required field
        formDataToSend.append('status', formData.status);
        if (formData.sub_client_id) {
          formDataToSend.append('sub_client_id', formData.sub_client_id.toString());
        }
        formDataToSend.append('general_notes', formData.general_notes || '');
        formDataToSend.append('damage_notes', formData.damage_notes || '');
        formDataToSend.append('alert_notes', formData.alert_notes || '');
        
        // Ajouter la signature si disponible
        // Use the passed parameter first, then fall back to state
        const fileToUse = signatureFileToUse !== undefined ? signatureFileToUse : signatureFile;
        if (fileToUse) {
          console.log('Adding signature to FormData:', {
            fileName: fileToUse.name,
            fileSize: fileToUse.size,
            fileType: fileToUse.type
          });
          formDataToSend.append('signature', fileToUse);
        } else {
          console.log('No signature file to add');
        }
        
        // Ajouter les photos
        // Filter out photos without files AND ensure they have types (required by backend)
        const photosWithFiles = formData.photos?.filter(photo => photo.file && photo.type) || [];
        if (photosWithFiles.length > 0) {
          photosWithFiles.forEach((photo, index) => {
            formDataToSend.append(`photos[${index}][type]`, photo.type);
            formDataToSend.append(`photos[${index}][description]`, photo.description || '');
            if (photo.file) {
              formDataToSend.append(`photos[${index}][file]`, photo.file);
            }
          });
        }
        
        // Log warnings for photos that were excluded
        const photosWithoutFiles = formData.photos?.filter(photo => !photo.file) || [];
        const photosWithoutTypes = formData.photos?.filter(photo => photo.file && !photo.type) || [];
        
        if (photosWithoutFiles.length > 0) {
          console.warn('Some photos were not included because they have no file:', photosWithoutFiles.map(p => ({ type: p.type, hasPath: !!p.photo_path })));
        }
        if (photosWithoutTypes.length > 0) {
          console.warn('Some photos were not included because they have no type:', photosWithoutTypes.map(p => ({ hasFile: !!p.file, hasPath: !!p.photo_path })));
          alert('Attention: Certaines photos n\'ont pas été incluses car elles n\'ont pas de type défini.');
        }
        
        // Ajouter les notes
        if (formData.notes && formData.notes.length > 0) {
          formData.notes.forEach((note, index) => {
            formDataToSend.append(`notes[${index}][type]`, note.type);
            formDataToSend.append(`notes[${index}][content]`, note.content);
          });
        }
        
        // Log FormData contents for debugging (without files)
        console.log('Submitting FormData:', {
          assessment_date: formData.assessment_date,
          assessment_type: formData.assessment_type,
          status: formData.status,
          sub_client_id: formData.sub_client_id,
          photosCount: photosWithFiles.length,
          notesCount: formData.notes?.length || 0,
          hasSignature: !!fileToUse
        });
        
        await dispatch(createAssessment({ vehicleId: vehicle.id, data: formDataToSend })).unwrap();
        
        // Reset signature after successful submission
        setSignatureDataUrl(null);
        setSignatureFile(null);
        
        // Show success message for new assessments
        setShowSuccessMessage(true);
        
        // Close modal after a short delay to show the success message
        setTimeout(() => {
          setShowSuccessMessage(false);
          onSuccess?.();
          onClose();
        }, 2500); // Show for 2.5 seconds before closing
        return; // Exit early
      }
      
      // For updates, close immediately
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving assessment:', error);
      
      // Handle both string errors (from rejectWithValue) and object errors
      let errorMessage = 'Erreur lors de la sauvegarde de l\'évaluation';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.status && error?.data) {
        // Handle validation errors from Laravel
        const validationErrors = error.data.errors || error.data;
        if (typeof validationErrors === 'object') {
          const errorList = Object.entries(validationErrors)
            .map(([key, value]: [string, any]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMessage = `Erreurs de validation:\n${errorList}`;
        } else {
          errorMessage = error.data.message || errorMessage;
        }
      }
      
      console.error('Full error details:', {
        error,
        errorType: typeof error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        data: error?.data
      });
      
      alert(`Erreur: ${errorMessage}\n\nVérifiez la console pour plus de détails.`);
    }
  };

  // Memoize the latest assessment to avoid recalculating on every render
  const latestAssessment = useMemo(() => {
    // assessmentHistory is grouped by date: { '2024-01-01': [...], '2024-01-02': [...] }
    // We need to flatten and sort by date (newest first)
    const allAssessments = Object.values(assessmentHistory).flat();
    
    // Sort by assessment_date descending (newest first), then by id descending
    const sorted = allAssessments.sort((a, b) => {
      const dateA = new Date(a.assessment_date).getTime();
      const dateB = new Date(b.assessment_date).getTime();
      if (dateB !== dateA) {
        return dateB - dateA; // Newest first
      }
      return (b.id || 0) - (a.id || 0); // If same date, newest ID first
    });
    
    const latest = sorted.length > 0 ? sorted[0] : null;
    
    // Debug: Log the latest assessment to verify notes are loaded (only once when it changes)
    if (latest) {
      console.log('Latest assessment for template loading:', {
        id: latest.id,
        date: latest.assessment_date,
        notesCount: latest.notes?.length || 0,
        photosCount: latest.photos?.length || 0,
        notes: latest.notes?.map(n => ({ id: n.id, type: n.type, content: n.content?.substring(0, 50) })) || []
      });
    }
    
    return latest;
  }, [assessmentHistory]);

  const renderGeneralTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Template Selector */}
      <Card sx={{ bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              📋 Template de l'état précédent
            </Typography>
            {latestAssessment && (
              <Button
                size="small"
                onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                variant="outlined"
              >
                {showTemplateSelector ? 'Masquer' : 'Charger'}
              </Button>
            )}
          </Box>
          
          {latestAssessment ? (
            showTemplateSelector && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dernier état des lieux: {format(new Date(latestAssessment.assessment_date), 'dd/MM/yyyy', { locale: fr })}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleLoadTemplate(latestAssessment)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  📋 Charger les photos et notes du {format(new Date(latestAssessment.assessment_date), 'dd/MM/yyyy', { locale: fr })}
                </Button>
              </Box>
            )
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              📝 Aucun état des lieux précédent trouvé. Ce sera le premier état pour ce véhicule.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Assessment Date */}
      <TextField
        label="Date d'évaluation"
        type="date"
        value={formData.assessment_date}
        onChange={(e) => handleInputChange('assessment_date', e.target.value)}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      {/* Assessment Type */}
      <FormControl fullWidth required>
        <InputLabel>Type d'évaluation *</InputLabel>
        <Select
          value={formData.assessment_type || ''}
          onChange={(e) => handleInputChange('assessment_type', e.target.value || null)}
          label="Type d'évaluation *"
          required
          error={!formData.assessment_type}
        >
          <MenuItem value="depart">Départ</MenuItem>
          <MenuItem value="retour">Retour</MenuItem>
        </Select>
      </FormControl>

      {/* Status */}
      <FormControl fullWidth>
        <InputLabel>Statut</InputLabel>
        <Select
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value)}
          label="Statut"
        >
          <MenuItem value="good">Bon</MenuItem>
          <MenuItem value="fair">Correct</MenuItem>
          <MenuItem value="poor">Mauvais</MenuItem>
          <MenuItem value="critical">Critique</MenuItem>
        </Select>
      </FormControl>

      {/* Sub-Client Selection */}
      <Card sx={{ bgcolor: '#f9f9f9', border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            👤 Sous-client
          </Typography>
          {selectedSubClient ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={selectedSubClient.type === 'company' ? 'Entreprise' : 'Individuel'}
                  size="small"
                  color={selectedSubClient.type === 'company' ? 'primary' : 'secondary'}
                />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSubClient.display_name || selectedSubClient.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedSubClient.email}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                onClick={() => {
                  setSelectedSubClient(null);
                  setFormData(prev => ({ ...prev, sub_client_id: null }));
                }}
                color="error"
              >
                Retirer
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowSubClientSearch(true)}
              sx={{ py: 1.5 }}
            >
              Rechercher ou créer un sous-client
            </Button>
          )}
          {selectedSubClient && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Un email sera envoyé à ce sous-client lors de la création de l'évaluation.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Helper function to get photo type label
  const getPhotoTypeLabel = (type: string) => {
    const photoType = photoTypeOptions.find(opt => opt.value === type);
    return photoType ? `${photoType.icon} ${photoType.label}` : type;
  };

  // Helper function to get all unique photo types from both depart and retour
  const getAllPhotoTypes = () => {
    const types = new Set<string>();
    // First, add all types from depart assessment (so we show them even if retour doesn't have them yet)
    if (departAssessment?.photos) {
      departAssessment.photos.forEach(photo => {
        if (photo.type) types.add(photo.type);
      });
    }
    // Then add types from retour photos (for types that might not be in depart)
    if (formData.photos) {
      formData.photos.forEach(photo => {
        if (photo.type) types.add(photo.type);
      });
    }
    // If no types found but we have depart photos, still return an empty array (will show "no photos" message)
    // If we have types, return them sorted
    return Array.from(types);
  };

  const renderPhotosTab = () => {
    // Comparison view should only appear when creating a Retour and we have a Depart reference
    const isRetourComparison = !isEditMode 
      && departAssessment 
      && formData.assessment_type === 'retour';
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Edit Mode Info Banner */}
        {isEditMode && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              📸 Mode modification
            </Typography>
            <Typography variant="body2">
              Les photos existantes sont affichées en lecture seule. Pour ajouter de nouvelles photos, créez un nouvel état des lieux.
            </Typography>
          </Alert>
        )}

        {/* Comparison Mode Info Banner */}
        {isRetourComparison && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              🔄 Mode Comparaison Départ ↔ Retour
            </Typography>
            <Typography variant="body2">
              Comparez les photos de départ (gauche) avec les photos de retour que vous prenez (droite).
            </Typography>
          </Alert>
        )}

        {photoTypesLoading && conditionPhotoTypes.length === 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Chargement des types de photos personnalisés...
          </Alert>
        )}
        
        {/* Add Photo Buttons - Disabled in Edit Mode */}
        {!isRetourComparison && (
          <Box sx={{ display: 'flex', gap: 2, alignSelf: 'flex-start' }}>
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => handleAddPhoto()}
              disabled={isEditMode}
            >
              {isEditMode ? 'Ajout désactivé' : 'Depuis fichier'}
            </Button>
            <Button
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={() => {
                setPhotoToUpdateIndex(null);
                setShowPhotoCapture(true);
              }}
              disabled={isEditMode}
            >
              {isEditMode ? 'Ajout désactivé' : 'Capturer photo'}
            </Button>
          </Box>
        )}

        {isRetourComparison ? (
          // Comparison View: Each photo type in a row with Depart and Retour side by side
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Headers */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2196f3', textAlign: 'center' }}>
                  📤 DÉPART
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff6b35', textAlign: 'center' }}>
                  📥 RETOUR
                </Typography>
              </Box>
            </Box>

            {/* Each photo type comparison - side by side */}
            {getAllPhotoTypes().map((photoType) => {
              const departPhoto = departAssessment.photos?.find(p => p.type === photoType);
              const retourPhotoIndex = formData.photos?.findIndex(p => p.type === photoType);
              const retourPhoto = retourPhotoIndex !== undefined && retourPhotoIndex >= 0 
                ? formData.photos?.[retourPhotoIndex] 
                : null;
              
              return (
                <Card key={photoType} sx={{ mb: 2 }}>
                  <CardContent>
                    {/* Photo Type Header */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, fontSize: '16px', textAlign: 'center', pb: 1, borderBottom: '2px solid #e0e0e0' }}>
                      {getPhotoTypeLabel(photoType)}
                    </Typography>

                    {/* Two columns side by side - Photos aligned */}
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                      {/* Depart Column - Photo only */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ mb: 1, textAlign: 'center', minHeight: '220px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                          {departPhoto && (departPhoto.file_url || departPhoto.file_path) ? (
                            <>
                              <img 
                                src={departPhoto.file_url || `/api/storage/vehicle-assessments/${departPhoto.file_path}`} 
                                alt={getPhotoTypeLabel(photoType)}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '200px', 
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: '2px solid #2196f3',
                                  padding: '4px',
                                  backgroundColor: 'white',
                                  margin: '0 auto'
                                }}
                              />
                              {departPhoto.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '11px' }}>
                                  {departPhoto.description}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: '8px', border: '2px dashed #2196f3', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {departPhoto ? 'Photo non disponible' : 'Aucune photo'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Retour Column - Photo aligned + controls below */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {retourPhoto ? (
                          <>
                            {/* Photo aligned with depart photo */}
                            <Box sx={{ mb: 1, textAlign: 'center', minHeight: '220px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                              {retourPhoto.photo_path ? (
                                <>
                                  <img 
                                    src={retourPhoto.photo_path} 
                                    alt={getPhotoTypeLabel(photoType)}
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: '200px', 
                                      objectFit: 'contain',
                                      borderRadius: '8px',
                                      border: '2px solid #ff6b35',
                                      padding: '4px',
                                      backgroundColor: 'white',
                                      margin: '0 auto'
                                    }}
                                  />
                                  {retourPhoto.description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '11px' }}>
                                      {retourPhoto.description}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Box 
                                  onClick={() => {
                                    setPhotoToUpdateIndex(retourPhotoIndex!);
                                    setShowPhotoCapture(true);
                                  }}
                                  sx={{ 
                                    p: 2,
                                    textAlign: 'center',
                                    bgcolor: '#fff5f0',
                                    borderRadius: '8px',
                                    border: '2px dashed #ff6b35',
                                    minHeight: '200px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      bgcolor: '#ffe8dc',
                                      borderColor: '#ff6b35',
                                    }
                                  }}
                                >
                                  <CameraIcon sx={{ fontSize: 36, color: '#ff6b35', mb: 0.5 }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '11px' }}>
                                    Cliquez pour ajouter
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Controls below photo */}
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <FormControl size="small" sx={{ flex: 1, mr: 1 }}>
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={retourPhoto.type}
                                    onChange={(e) => handleUpdatePhoto(retourPhotoIndex!, 'type', e.target.value)}
                                    label="Type"
                                  >
                                    {photoTypeOptions.map(type => (
                                      <MenuItem key={type.value} value={type.value}>
                                        {type.icon} {type.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemovePhoto(retourPhotoIndex!)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              
                              {retourPhoto.photo_path && !isEditMode && (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenPhotoEditor(retourPhotoIndex!);
                                    }}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                  >
                                    Annoter
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPhotoToUpdateIndex(retourPhotoIndex!);
                                      handleAddPhoto(retourPhotoIndex!);
                                    }}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                  >
                                    Remplacer
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<CameraIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPhotoToUpdateIndex(retourPhotoIndex!);
                                      setShowPhotoCapture(true);
                                    }}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                  >
                                    Capturer
                                  </Button>
                                </Box>
                              )}

                              <TextField
                                label="Description"
                                size="small"
                                multiline
                                rows={2}
                                value={retourPhoto.description || ''}
                                onChange={(e) => handleUpdatePhoto(retourPhotoIndex!, 'description', e.target.value)}
                                fullWidth
                                placeholder="Description..."
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </>
                        ) : (
                          <Box 
                            onClick={() => {
                              const newPhoto: VehicleConditionPhotoFormData = {
                                photo_path: '',
                                type: photoType,
                                description: '',
                              };
                              setFormData(prev => ({
                                ...prev,
                                photos: [...(prev.photos || []), newPhoto]
                              }));
                              setPhotoToUpdateIndex((formData.photos?.length || 0));
                              setShowPhotoCapture(true);
                            }}
                            sx={{ 
                              p: 3,
                              border: '2px dashed #ff6b35',
                              borderRadius: '8px',
                              bgcolor: '#fff5f0',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.2s',
                              minHeight: '220px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                bgcolor: '#ffe8dc',
                                borderColor: '#ff6b35',
                              }
                            }}
                          >
                            <CameraIcon sx={{ fontSize: 48, color: '#ff6b35', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Ajouter photo de retour
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Add Photo Buttons for unmatched types */}
            {getAllPhotoTypes().length === 0 && (
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => handleAddPhoto()}
                  fullWidth
                >
                  Depuis fichier
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CameraIcon />}
                  onClick={() => {
                    setPhotoToUpdateIndex(null);
                    setShowPhotoCapture(true);
                  }}
                  fullWidth
                  sx={{ backgroundColor: '#ff6b35', '&:hover': { backgroundColor: '#e55a2b' } }}
                >
                  Capturer photo
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          // Normal View: Single column
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {formData.photos?.map((photo, index) => (
                <Box key={index} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Photo {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePhoto(index)}
                          color="error"
                          disabled={isEditMode}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={isEditMode}>
                        <InputLabel>Type de photo</InputLabel>
                        <Select
                          value={photo.type}
                          onChange={(e) => handleUpdatePhoto(index, 'type', e.target.value)}
                          label="Type de photo"
                          disabled={isEditMode}
                        >
                          {photoTypeOptions.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </MenuItem>
                          ))}
                          {photo.type && !photoTypeOptions.some(type => type.value === photo.type) && (
                            <MenuItem value={photo.type}>
                              {photo.type}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>

                      {photo.photo_path ? (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <img 
                            src={photo.photo_path} 
                            alt={`Photo ${index + 1}`}
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '200px', 
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }}
                          />
                          {!isEditMode && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPhotoEditor(index);
                                }}
                              >
                                Annoter
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<PhotoCameraIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPhotoToUpdateIndex(index);
                                  handleAddPhoto(index);
                                }}
                              >
                                Remplacer
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<CameraIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPhotoToUpdateIndex(index);
                                  setShowPhotoCapture(true);
                                }}
                              >
                                Capturer
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : photo.type ? (
                        <Box 
                          onClick={() => {
                            if (!isEditMode) {
                              setPhotoToUpdateIndex(index);
                              setShowPhotoCapture(true);
                            }
                          }}
                          sx={{ 
                            mb: 2, 
                            textAlign: 'center',
                            p: 3,
                            border: '2px dashed #ccc',
                            borderRadius: '8px',
                            bgcolor: '#f5f5f5',
                            cursor: isEditMode ? 'default' : 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': isEditMode ? {} : {
                              bgcolor: '#e8e8e8',
                              borderColor: '#ff6b35',
                              transform: 'scale(1.02)'
                            }
                          }}
                        >
                          <CameraIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {isEditMode ? 'Photo à ajouter' : 'Cliquez pour ajouter une photo'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Type et description chargés depuis le template
                          </Typography>
                          {!isEditMode && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PhotoCameraIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPhotoToUpdateIndex(index);
                                  handleAddPhoto(index);
                                }}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Fichier
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CameraIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPhotoToUpdateIndex(index);
                                  setShowPhotoCapture(true);
                                }}
                                sx={{ fontSize: '0.75rem', backgroundColor: '#ff6b35', '&:hover': { backgroundColor: '#e55a2b' } }}
                              >
                                Capturer
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      <TextField
                        label="Description"
                        size="small"
                        multiline
                        rows={2}
                        value={photo.description}
                        onChange={(e) => handleUpdatePhoto(index, 'description', e.target.value)}
                        fullWidth
                        placeholder="Description de la photo..."
                        disabled={isEditMode}
                        InputProps={{
                          readOnly: isEditMode,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {(!formData.photos || formData.photos.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Aucune photo ajoutée
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cliquez sur "Ajouter une photo" pour commencer
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderNotesTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Edit Mode Info Banner */}
      {isEditMode && (
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            📝 Mode modification
          </Typography>
          <Typography variant="body2">
            Les notes existantes sont affichées en lecture seule. Pour ajouter de nouvelles notes, créez un nouvel état des lieux.
          </Typography>
        </Alert>
      )}
      
      {/* Add Note Button - Disabled in Edit Mode */}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddNote}
        disabled={isEditMode} // ✅ Disable in edit mode
        sx={{ alignSelf: 'flex-start' }}
      >
        {isEditMode ? 'Ajout de notes désactivé en modification' : 'Ajouter une note'}
      </Button>

      {/* Notes List */}
      {formData.notes?.map((note, index) => (
        <Card key={index}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={noteTypes.find(t => t.value === note.type)?.label}
                  size="small"
                  sx={{
                    backgroundColor: noteTypes.find(t => t.value === note.type)?.color,
                    color: 'white'
                  }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Note {index + 1}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemoveNote(index)}
                color="error"
                disabled={isEditMode} // ✅ Disable delete in edit mode
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Note Type */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={isEditMode}>
              <InputLabel>Type de note</InputLabel>
              <Select
                value={note.type}
                onChange={(e) => handleUpdateNote(index, 'type', e.target.value)}
                label="Type de note"
                disabled={isEditMode} // ✅ Disable in edit mode
              >
                {noteTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Note Content */}
            <TextField
              label="Contenu de la note"
              multiline
              rows={3}
              value={note.content}
              onChange={(e) => handleUpdateNote(index, 'content', e.target.value)}
              fullWidth
              placeholder="Saisissez votre note..."
              disabled={isEditMode} // ✅ Disable in edit mode
              InputProps={{
                readOnly: isEditMode, // ✅ Make read-only in edit mode
              }}
            />
          </CardContent>
        </Card>
      ))}

      {(!formData.notes || formData.notes.length === 0) && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Aucune note ajoutée
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cliquez sur "Ajouter une note" pour commencer
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: { borderRadius: 3, m: 2, height: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditMode ? '✏️ Modifier État des Lieux' : '📸 Nouvel État des Lieux'} - {vehicle.registration_number}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Success Alert */}
        {showSuccessMessage && !isEditMode && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              ✅ État des lieux créé avec succès !
            </Typography>
            <Typography variant="body2">
              L'email de notification a été envoyé au client.
            </Typography>
          </Alert>
        )}
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tab Navigation */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button
            variant={activeTab === 'general' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('general')}
            startIcon={<EditIcon />}
          >
            Général
          </Button>
          <Button
            variant={activeTab === 'photos' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('photos')}
            startIcon={<CameraIcon />}
          >
            Photos ({formData.photos?.length || 0})
          </Button>
          <Button
            variant={activeTab === 'notes' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('notes')}
            startIcon={<EditIcon />}
          >
            Notes ({formData.notes?.length || 0})
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'photos' && renderPhotosTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          startIcon={<CancelIcon />}
          disabled={creating}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={(creating || updating) ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={creating || updating}
          sx={{ backgroundColor: '#ff6b35', '&:hover': { backgroundColor: '#e55a2b' } }}
        >
          {creating || updating 
            ? (isEditMode ? 'Modification...' : 'Création...') 
            : (isEditMode ? 'Modifier l\'état des lieux' : 'Créer l\'état des lieux')
          }
        </Button>
      </DialogActions>

      {/* Photo Capture Modal */}
      <PhotoCaptureModal
        open={showPhotoCapture}
        onClose={() => {
          setShowPhotoCapture(false);
          setPhotoToUpdateIndex(null); // Reset update index when modal closes
        }}
        onCapture={handleCapturePhoto}
      />
      {isPhotoEditorOpen && photoEditorImage && (
        <PhotoEditModal
          open={isPhotoEditorOpen}
          onClose={handleClosePhotoEditor}
          imageSrc={photoEditorImage}
          onSave={handlePhotoEditSave}
        />
      )}

      {/* Sub-Client Search Modal */}
      <SubClientSearchModal
        open={showSubClientSearch}
        onClose={() => setShowSubClientSearch(false)}
        onSelect={(subClient) => {
          setSelectedSubClient(subClient);
          setFormData(prev => ({ ...prev, sub_client_id: subClient.id }));
        }}
        onCreateNew={() => {
          setShowSubClientSearch(false);
          setShowSubClientCreate(true);
        }}
      />

      {/* Sub-Client Create Modal */}
      <SubClientCreateModal
        open={showSubClientCreate}
        onClose={() => setShowSubClientCreate(false)}
        onSuccess={(subClient) => {
          setSelectedSubClient(subClient);
          setFormData(prev => ({ ...prev, sub_client_id: subClient.id }));
        }}
      />

      {/* Signature Modal */}
      <SignatureModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
        title="Signature du client"
        subtitle="Veuillez demander au client de signer avant de soumettre l'état des lieux"
      />
    </Dialog>

  </>
  );
};

export default ConditionAssessmentModal;
